import React, { useRef, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import api from "@/services/api";
import toast from "react-hot-toast";

export default function AvatarUpload({ user, onSave }) {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setIsUploading(true);
    
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        try {
          // Upload to S3
          const uploadRes = await api.post("/file", {
            file: {
              rawBody: reader.result,
              name: file.name,
            },
            folder: "/avatars",
          });

          if (!uploadRes.ok) {
            throw new Error(uploadRes.code || "Failed to upload image");
          }

          // Save profile with new avatar URL
          await onSave({ avatar: uploadRes.data });
          toast.success("Avatar updated successfully");
        } catch (error) {
          console.error("Upload error:", error);
          toast.error(error.message || "Failed to upload avatar");
        } finally {
          setIsUploading(false);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      };

      reader.onerror = () => {
        toast.error("Failed to read file");
        setIsUploading(false);
      };
    } catch (error) {
      console.error("File handling error:", error);
      toast.error("Failed to process file");
      setIsUploading(false);
    }
  };

  return (
    <div className="relative">
      <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-3xl font-bold border-4 border-white shadow-sm overflow-hidden">
        {user.avatar ? (
          <img src={user.avatar} alt={user.firstname} className="w-full h-full object-cover" />
        ) : (
          <span>{user.firstname?.[0]}{user.lastname?.[0]}</span>
        )}
        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      <button
        onClick={handleEditClick}
        disabled={isUploading}
        className="absolute bottom-0 right-0 p-1.5 bg-white border border-gray-200 rounded-full text-gray-600 hover:text-blue-600 shadow-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FiEdit2 className="w-4 h-4" />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}

